// src/constants/crisisSupport.js
// Crisis support and online-safety reporting by country, for every crisis
// and reporting listing in Friendships Vibe. The app serves seven markets:
// Australia, New Zealand, India, the UK, Ireland, Canada and the US.
//
// ⚠️ The crisis numbers are not kept here. They live in the fleet's one
// table, packages/crisis-support/src/services.js, with the date each was
// verified and the page it was verified on. Change a number or its hours
// THERE, never by overriding it here. This app shows the 'young' list, as the
// other Vibe apps do.
//
// ⚠️ Never fall back to another country's list. Lookups return null for
// an unknown region so the UI can say "we don't hold numbers for your
// region — call your local emergency number" rather than dial a number
// that reaches nobody.
//
// CYBERBULLYING_REPORTING (this app's own table, below) last verified:
// 5 September 2026.
//
// Imported by relative path, not a Vite alias: the Knowledge Hub importer
// loads this file in plain Node to index it (appRegistry.json, `safety`).

import {
  crisisSupportTable,
  serviceById,
  countryPreference,
  normaliseCountryKey,
  emergencyCallAdvice,
  detectCountry,
  DIRECTORIES,
  COUNTRY_KEYS,
  DEFAULT_COUNTRY
} from '../../../../packages/crisis-support/src/index.js';

export const CRISIS_SUPPORT = crisisSupportTable('young');

export const CYBERBULLYING_REPORTING = {
  australia: {
    name: 'Australia',
    body: 'eSafety Commissioner',
    website: 'esafety.gov.au/report',
    canCompelRemoval: true,
    detail:
      'Report it to the app or site first. If they have not taken it down, eSafety can investigate and make them remove it, and can tell the person responsible to stop or apologise. It is free, and you can report it yourself.'
  },
  newzealand: {
    name: 'New Zealand',
    body: 'Netsafe',
    number: '0508 638 723',
    website: 'netsafe.org.nz',
    canCompelRemoval: true,
    detail:
      'Free and confidential, Monday to Friday 9am-5pm. You can also text "Netsafe" to 4282. Netsafe is the approved agency under the Harmful Digital Communications Act, so it can take your case to the platform and further if it has to.'
  },
  uk: {
    name: 'United Kingdom',
    body: 'Report Harmful Content',
    website: 'reportharmfulcontent.com',
    canCompelRemoval: false,
    detail:
      'For anyone 13 or over in the UK, run by the UK Safer Internet Centre. Report to the platform first; if nothing happens, they will review it and take it up with the platform for you. You can report any time and they answer within about three days.'
  },
  ireland: {
    name: 'Ireland',
    body: 'Coimisiun na Mean',
    website: 'cnam.ie',
    canCompelRemoval: false,
    detail:
      "Report to the platform first - under Ireland's Online Safety Code they have to give you a way to do that and to act on it. Coco's Law also makes serious online harassment a criminal offence. ISPCC Childline (1800 66 66 66) is free and 24/7 if you want to talk it through first."
  },
  canada: {
    name: 'Canada',
    body: 'NeedHelpNow.ca',
    website: 'needhelpnow.ca',
    canCompelRemoval: false,
    detail:
      'Step-by-step help getting content taken down, from the Canadian Centre for Child Protection. Kids Help Phone (1-800-668-6868, or text CONNECT to 686868) is free and 24/7.'
  },
  usa: {
    name: 'United States',
    body: 'StopBullying.gov',
    website: 'stopbullying.gov',
    canCompelRemoval: false,
    detail:
      'There is no national takedown scheme - removal goes through the platform, and through your school, which in most states has to act on bullying that affects you at school. Crisis Text Line (text HOME to 741741) is free and 24/7.'
  },
  india: {
    name: 'India',
    body: 'National Cyber Crime Reporting Portal',
    website: 'cybercrime.gov.in',
    canCompelRemoval: false,
    detail:
      'Report to the platform first. The national portal handles serious matters - threats, harassment, and anything sexual involving someone under 18 - and you can report anonymously. Tele-MANAS (14416) is a free government helpline if you need to talk to someone.'
  }
};

// Nude or sexual images of anyone under 18: where to report them so they come
// down, by country. Each was read on the service's own site on 2 October 2026
// (`source`). No phone numbers here on purpose: the numbers a reader dials
// come from the shared table above, and these are reporting routes, not
// helplines.
//
// The rule every surface that uses this must carry, from eSafety's own
// evidence guidance (esafety.gov.au/report/how-to-collect-evidence): never
// screenshot, save or share the image itself, even as proof. Record where it
// is (the app, the link, the username, the date) instead. Several of these
// services' own pages give general "screenshot the content" advice with no
// under-18 exception, so don't copy their evidence advice into the app.
export const INTIMATE_IMAGE_REPORTING_VERIFIED_ON = '2026-10-02';

export const INTIMATE_IMAGE_REPORTING = {
  australia: {
    name: 'Australia',
    services: [
      {
        name: 'eSafety Commissioner',
        website: 'esafety.gov.au/report',
        detail: 'If a nude or sexual image of you has been shared, or someone is threatening to share one, eSafety can help get it taken down and work out what to do next. You can report it yourself.',
        source: 'https://www.esafety.gov.au/young-people/my-nudes-have-been-shared'
      },
      {
        name: 'ACCCE (the Australian Centre to Counter Child Exploitation)',
        website: 'accce.gov.au/report',
        detail: 'The police team to tell if someone is pressuring you for nudes, or threatening you or asking for money over them.',
        source: 'https://www.accce.gov.au/report'
      }
    ]
  },
  newzealand: {
    name: 'New Zealand',
    services: [
      {
        name: 'Netsafe',
        website: 'netsafe.org.nz',
        detail: 'Free and confidential. Netsafe can often help get a nude taken down and talk you through your options. It only needs the link, never a copy of the image.',
        source: 'https://www.netsafe.org.nz/online-abuse-and-harassment/image-based-abuse'
      }
    ]
  },
  uk: {
    name: 'United Kingdom',
    services: [
      {
        name: 'Report Remove (Childline and the Internet Watch Foundation)',
        website: 'iwf.org.uk/our-technology/report-remove',
        detail: 'If you are under 18 in the UK, you can report a nude of yourself and ask for it to be removed from the internet. Free and confidential.',
        source: 'https://www.iwf.org.uk/our-technology/report-remove/'
      }
    ]
  },
  ireland: {
    name: 'Ireland',
    services: [
      {
        name: 'Hotline.ie',
        website: 'hotline.ie/report',
        detail: "Ireland's national service for reporting sexual images of anyone under 18 so they can be taken down. You can report it yourself, or ask a trusted adult to do it with you.",
        source: 'https://hotline.ie/faqs/'
      }
    ]
  },
  canada: {
    name: 'Canada',
    services: [
      {
        name: 'NeedHelpNow.ca',
        website: 'needhelpnow.ca',
        detail: 'For anyone under 18 whose nude has been shared: steps to get it removed, and a team that can contact the apps for you. From the Canadian Centre for Child Protection.',
        source: 'https://www.needhelpnow.ca/en/'
      },
      {
        name: 'Cybertip.ca',
        website: 'cybertip.ca/en/report',
        detail: "Canada's national tipline for reporting sexual images of anyone under 18, sextortion and luring. Staffed on weekdays.",
        source: 'https://www.cybertip.ca/en/report/'
      }
    ]
  },
  usa: {
    name: 'United States',
    services: [
      {
        name: 'NCMEC CyberTipline',
        website: 'report.cybertip.org',
        detail: 'The US place to report sexual images or exploitation of anyone under 18 online. Open 24/7, and you can report about yourself.',
        source: 'https://www.missingkids.org/gethelpnow/cybertipline'
      }
    ]
  },
  india: {
    name: 'India',
    services: [
      {
        name: 'National Cyber Crime Reporting Portal',
        website: 'cybercrime.gov.in',
        detail: "The government's site for reporting cybercrime, including sexual images of anyone under 18. Choose 'Women/Children Related Crime'.",
        source: 'https://cybercrime.gov.in/Webform/FAQ.aspx'
      }
    ]
  }
};

// Works in every country, for an image taken before the person was 18.
export const TAKE_IT_DOWN = {
  name: 'Take It Down (from NCMEC)',
  website: 'takeitdown.ncmec.org',
  detail: "Free, for anyone anywhere in the world. If it's a picture of you from before you were 18 and you still have it on your phone, Take It Down makes a digital fingerprint of it on your phone, so the picture itself never leaves it. The apps that take part then look for copies and remove them.",
  source: 'https://takeitdown.ncmec.org/faq/'
};

export { COUNTRY_KEYS, DEFAULT_COUNTRY, DIRECTORIES, normaliseCountryKey, emergencyCallAdvice, serviceById, detectCountry };

// Where the reader's country choice lives, shared by every surface that shows
// numbers (the coach's crisis footer, and any page that offers a picker).
export const COUNTRY_PREF_KEY = 'friendships-vibe_crisis_country';

export const { readCountryPref, writeCountryPref, initialCountry } = countryPreference(COUNTRY_PREF_KEY);

/**
 * The reader's country only when we actually know it: the one they chose,
 * else their browser's region. Null when it is neither of those, so a surface
 * asks "where are you?" rather than quietly showing Australian numbers to
 * someone in Manchester or Toronto. (initialCountry() falls back to Australia
 * as a labelled example; the help surfaces in the tools don't.)
 */
export const knownCountry = () => readCountryPref() || detectCountry() || null;

/** Reporting routes for intimate images of under-18s. Fails closed, like the rest. */
export function getIntimateImageReporting(country) {
  return INTIMATE_IMAGE_REPORTING[normaliseCountryKey(country)] || null;
}

/**
 * Look up crisis support for a region.
 *
 * Fails CLOSED. An unknown or missing region returns null so callers can
 * say "we don't hold numbers for your region" and render no tel: link.
 * Never hand someone another country's crisis line.
 */
export function getCrisisSupport(country) {
  return CRISIS_SUPPORT[normaliseCountryKey(country)] || null;
}

/** Look up cyberbullying reporting for a region. Fails closed, as above. */
export function getCyberbullyingReporting(country) {
  return CYBERBULLYING_REPORTING[normaliseCountryKey(country)] || null;
}
